// package imports
import { KitButton, KitCard } from 'components/common/cms-kit';
import { useTheme } from '@mui/material/styles';
import { Box, Tooltip, Typography, useMediaQuery } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Theme as MuiTheme } from '@mui/material/styles';

// local imports
import hex2rgba from 'services/colorUtils/hex2rgba';
import { themeColor } from '../../../services/styleUtils/colorUtils';
import { logClientException } from '../../../appinsights/clientAppInsights';
import { callApi } from '../../../services/apiWrapper';
import { SecureFileRecord } from 'types/SecureFileTypes';
import { User } from 'next-auth';

type FileCardProps = {
  file: SecureFileRecord;
  user?: User;
  tabBackGroundColor: string;
  buttonText: string;
};

const FileCard = ({
  file,
  user,
  tabBackGroundColor,
  buttonText,
}: FileCardProps) => {
  const theme = useTheme() as MuiTheme;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const getTagList = () => {
    if (!file.tags) {
      return;
    }

    // distinct list of tags
    const allTags: string[] = [];
    Object.keys(file.tags).forEach((key) => {
      file.tags[key]?.forEach((tag) => {
        if (!allTags.includes(tag)) {
          allTags.push(tag);
        }
      });
    });
    let tagList = allTags.map((b) => {
      return (
        <span style={{ marginRight: '10px' }} key={b}>
          {b}
        </span>
      );
    });

    if (isSmallScreen) {
      return tagList;
    }
    if (!isSmallScreen) {
      const textList = allTags.slice();

      // determine the number of tags which can fit on a line
      let maxTags = textList.length;
      for (let i = 1; i <= textList.length; i++) {
        let temp = textList.slice(0, i).join(', ');
        if (temp.length > 60) {
          maxTags = i;
          i = textList.length + 1;
        } else if (i < textList.length) {
          temp = textList.slice(0, i + 1).join(', ');
          if (temp.length > 60) {
            maxTags = i;
            i = textList.length + 1;
          }
        }
      }

      if (tagList.length <= maxTags) {
        return tagList;
      }

      const tooltipTags = textList.slice(maxTags).join(', ');
      tagList = tagList.slice(0, maxTags);
      tagList.push(
        <Tooltip key="more-tags" title={tooltipTags} arrow>
          <MoreHorizIcon />
        </Tooltip>,
      );
      return tagList;
    }
  };

  const handleViewClick = () => {
    if (user) {
      const body = {
        user,
        event: 'open_secure_file',
        additionalInfo: file.file_name,
      };
      try {
        const url = '/api/db/report-event-user';
        const data = JSON.stringify(body);
        if (navigator.sendBeacon) {
          const blob = new Blob([data], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        } else {
          // Fire and forget, don't await
          callApi(url, 'POST', body);
        }
      } catch (e) {
        logClientException(e);
      }
    }
  };

  return (
    <>
      <KitCard
        className="behaviorCard"
        style={{ width: '95%', borderRadius: '2px' }}
      >
        <div
          style={{
            borderTopLeftRadius: '2px',
            borderTopRightRadius: '2px',
            backgroundColor: hex2rgba(theme.palette.tertiary.main, 0.1),
            height: isSmallScreen ? '100%' : '60px',
            display: 'flex',
            alignItems: 'center',
            paddingRight: '15px',
            paddingTop: '15px',
            paddingBottom: '20px',
          }}
        >
          <Box
            sx={[
              {
                maskImage: "url('/assets/rectangle-cbcr.svg')",
                width: '12.5px',
                height: '36px',
                color: '#4AA00',
                position: 'absolute',
                top: 9,
                left: -10,
                zIndex: -1,
              },
              tabBackGroundColor
                ? {
                    backgroundColor: themeColor(theme, tabBackGroundColor),
                  }
                : {
                    backgroundColor: theme.palette.gold.main,
                  },
            ]}
          />
          <Tooltip title={file.friendly_file_name} arrow>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '21px',
                marginLeft: '45px',
              }}
            >
              {file.friendly_file_name}
            </Typography>
          </Tooltip>
        </div>

        <Box sx={{ margin: '15px 45px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <span
              style={{
                textTransform: 'uppercase',
                fontSize: '16px',
                lineHeight: '22px',
                fontWeight: 600,
                color: theme.palette.primary.light,
              }}
            >
              <a
                href={`/secure-file?fileId=${encodeURIComponent(file.file_name)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <KitButton
                  color="primary"
                  onClick={handleViewClick}
                  round
                  style={{
                    marginBottom: '5px',
                    width: 108,
                    height: 32,
                    borderRadius: '41px',
                  }}
                >
                  {buttonText}
                </KitButton>
              </a>
            </span>
          </Box>
        </Box>
        <hr
          style={{
            borderTop: `1px dashed ${theme.palette.grey['A400']}`,
            width: '100%',
          }}
        />
        <Box
          sx={{
            margin: '15px 30px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              fontSize: '13px',
              lineHeight: '22px',
              fontWeight: 400,
              color: theme.palette.text.medium,
              textTransform: 'capitalize',
            }}
          >
            {getTagList()}
          </div>
        </Box>
      </KitCard>
    </>
  );
};

export default FileCard;
